import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';

import { PatientsListColumnName, SortByFunctionType } from '@/shared/config';
import { getAgeGroup, parseDateAsLocal, trackEvent } from '@/shared/lib';

import { PatientsFilter, PatientsFiltersType } from '@/entities/patient';

import {
  AllPatientsFilterSettingsType,
  SharedByMeFilterSettingsType,
  SharedWithMeFilterSettingsType,
  SortByStateType,
} from '../config/types';
import { getSortByOptions } from '../utils/getSortByOptions';
import { getSortByOptionsForSharingBy } from '../utils/getSortByOptionsForSharingBy';
import { getSortByOptionsForSharingWith } from '../utils/getSortByOptionsForSharingWith';

import { usePatientsListStream } from './usePatientsListStream';
import { useSharedByMeStream } from './useSharedByMeStream';
import { useSharedWithMeStream } from './useSharedWithMeStream';

export type UsePatientsSortAndFiltersArgs = {
  organizationID: string;
  isAllPatientList: boolean;
  isSharedWithMeList: boolean;
  isSharedByMeOrSharedByStaffList: boolean;
};

// TODO: [16|h] For some reason patient list stream is not closing on unmount which it leads to infinite potential to duplicate opened streams
// This is very important to fix this issue because it could lead to serious performance issues
export const usePatientsSortAndFilters = ({
  organizationID,
  isAllPatientList,
  isSharedWithMeList,
  isSharedByMeOrSharedByStaffList,
}: UsePatientsSortAndFiltersArgs) => {
  const [searchParams] = useSearchParams();

  const [sortByState, setSortByState] = useState<SortByStateType>({
    sortKey: PatientsListColumnName.Default,
    leastOrGreatest: false,
  });

  const searchValue = searchParams.get('search') ?? '';

  const doctorFilters = searchParams.get('doctorIDs')?.split(',');
  const studyTypesFilters = searchParams.get('studyTypes')?.split(',');
  const ageGroupFilters = searchParams.get('ageGroup')?.split(',');

  const ageGroup = getAgeGroup(ageGroupFilters);
  const studyTypes = studyTypesFilters;

  const lastModifiedFromParam = searchParams.get('lastModifiedFrom');
  const lastModifiedToParam = searchParams.get('lastModifiedTo');
  const lastModifiedFrom = lastModifiedFromParam
    ? parseDateAsLocal(lastModifiedFromParam)
    : undefined;
  const lastModifiedTo = lastModifiedToParam
    ? parseDateAsLocal(lastModifiedToParam)
    : undefined;

  const hasTranscriptorParam = searchParams.get('hasTranscriptor');
  const hasTranscriptor = hasTranscriptorParam
    ? hasTranscriptorParam === 'true'
    : undefined;

  const { openPatientListStream, closePatientListStream } =
    usePatientsListStream({ organizationID });

  const { openSharedWithMeStream, closeSharedWithMeStream } =
    useSharedWithMeStream({ organizationID, searchValue });

  const { openSharedByMeStream, closeSharedByMeStream } = useSharedByMeStream({
    organizationID,
    searchValue,
  });

  const fetchMorePatients = (id: string) => {
    if (!organizationID || !id) {
      return;
    }

    if (isAllPatientList) {
      closePatientListStream(false);

      openPatientListStream({
        id,
        doctorFilters,
        searchString: searchValue,
        sortBy: getSortByOptions(
          sortByState?.sortKey,
          sortByState?.leastOrGreatest,
        ),
        studyTypes,
        ageGroup,
        lastModifiedFrom,
        lastModifiedTo,
        hasTranscriptor,
      });
    }

    if (isSharedWithMeList) {
      closeSharedWithMeStream(false);

      openSharedWithMeStream({
        searchString: searchValue,
        id,
        sortBy: getSortByOptionsForSharingWith(
          sortByState?.sortKey,
          sortByState?.leastOrGreatest,
        ),
      });
    }

    if (isSharedByMeOrSharedByStaffList) {
      closeSharedByMeStream(false);

      openSharedByMeStream({
        searchString: searchValue,
        id,
        sortBy: getSortByOptionsForSharingBy(
          sortByState?.sortKey,
          sortByState?.leastOrGreatest,
        ),
      });
    }
  };

  const sortByForAllPatients: SortByFunctionType = (
    sortKey,
    leastOrGreatest,
  ) => {
    if (!organizationID) {
      return;
    }

    const filterSettings: AllPatientsFilterSettingsType = {
      searchString: searchValue,
      id: '',
      doctorFilters,
      sortBy: getSortByOptions(sortKey, leastOrGreatest),
      studyTypes,
      ageGroup,
      lastModifiedFrom,
      lastModifiedTo,
      hasTranscriptor,
    };

    setSortByState({ sortKey, leastOrGreatest });

    if (isAllPatientList) {
      closePatientListStream(false);

      openPatientListStream(filterSettings);
    }
  };

  const sortByForSharedWithMe: SortByFunctionType = (
    sortKey,
    leastOrGreatest,
  ) => {
    if (!organizationID) {
      return;
    }

    const filterSettings: SharedWithMeFilterSettingsType = {
      searchString: searchValue,
      id: '',
      sortBy: getSortByOptionsForSharingWith(sortKey, leastOrGreatest),
    };

    setSortByState({ sortKey, leastOrGreatest });

    if (isSharedWithMeList) {
      closeSharedWithMeStream();

      openSharedWithMeStream(filterSettings);
    }
  };

  const sortByForSharedByMe: SortByFunctionType = (
    sortKey,
    leastOrGreatest,
  ) => {
    if (!organizationID) {
      return;
    }

    const filterSettings: SharedByMeFilterSettingsType = {
      searchString: searchValue,
      id: '',
      sortBy: getSortByOptionsForSharingBy(sortKey, leastOrGreatest),
    };

    setSortByState({ sortKey, leastOrGreatest });

    if (isSharedByMeOrSharedByStaffList) {
      closeSharedByMeStream();

      openSharedByMeStream(filterSettings);
    }
  };

  const searchPatients = (searchString: string) => {
    if (!organizationID && !isSharedWithMeList) {
      return;
    }

    if (isAllPatientList) {
      closePatientListStream();

      openPatientListStream({
        searchString,
        id: '',
        doctorFilters,
        studyTypes,
        ageGroup,
        lastModifiedFrom,
        lastModifiedTo,
        hasTranscriptor,
      });
    }

    if (isSharedWithMeList) {
      closeSharedWithMeStream();

      openSharedWithMeStream({
        searchString,
        id: '',
        sortBy: getSortByOptionsForSharingWith(
          sortByState?.sortKey,
          sortByState?.leastOrGreatest,
        ),
      });
    }

    if (isSharedByMeOrSharedByStaffList) {
      closeSharedByMeStream();

      openSharedByMeStream({
        searchString,
        id: '',
        sortBy: getSortByOptionsForSharingBy(
          sortByState?.sortKey,
          sortByState?.leastOrGreatest,
        ),
      });
    }
  };

  const filterByStudyTypes = (
    studyTypes: string[],
    hasTranscriptorRecords: boolean | undefined,
  ) => {
    closePatientListStream();
    openPatientListStream({
      searchString: searchValue,
      doctorFilters,
      id: '',
      studyTypes,
      ageGroup,
      lastModifiedFrom,
      lastModifiedTo,
      hasTranscriptor: hasTranscriptorRecords,
    });
  };

  const filterByAgeGroup = (ageGroup: string[]) => {
    closePatientListStream();
    openPatientListStream({
      searchString: searchValue,
      doctorFilters,
      id: '',
      studyTypes,
      ageGroup: getAgeGroup(ageGroup),
      lastModifiedFrom,
      lastModifiedTo,
      hasTranscriptor,
    });
  };

  const filterByDoctors = (doctorsIDs: string[]) => {
    closePatientListStream();

    openPatientListStream({
      searchString: searchValue,
      doctorFilters: doctorsIDs,
      id: '',
      studyTypes,
      ageGroup,
      lastModifiedFrom,
      lastModifiedTo,
      hasTranscriptor,
    });
  };

  const filterByLastModified = async (dates: { from?: Date; to?: Date }) => {
    closePatientListStream();

    await new Promise((resolve) => setTimeout(resolve, 100));

    openPatientListStream({
      searchString: searchValue,
      doctorFilters,
      id: '',
      studyTypes,
      ageGroup,
      lastModifiedFrom: dates.from,
      lastModifiedTo: dates.to,
      hasTranscriptor,
    });
  };

  const onFilterPatientsChange = (patientFilterTab: PatientsFiltersType) => {
    if (!organizationID) {
      return;
    }

    if (patientFilterTab === PatientsFilter.sharedWithMe) {
      trackEvent({
        eventName: 'PatientsPageReceivedReferralsClicked',
      });
    }

    if (
      patientFilterTab === PatientsFilter.sharedByStaff ||
      patientFilterTab === PatientsFilter.sharedByMe
    ) {
      trackEvent({
        eventName: 'PatientsPageSentReferralsClicked',
      });
    }
  };

  const closeStreams = useCallback(() => {
    closePatientListStream();
    closeSharedWithMeStream();
    closeSharedByMeStream();
  }, [closePatientListStream, closeSharedByMeStream, closeSharedWithMeStream]);

  useEffect(() => {
    if (organizationID) {
      closeStreams();

      if (isAllPatientList) {
        openPatientListStream({
          searchString: searchValue,
          id: '',
          doctorFilters,
          studyTypes,
          ageGroup,
          lastModifiedFrom,
          lastModifiedTo,
          hasTranscriptor,
        });
      } else if (isSharedWithMeList) {
        openSharedWithMeStream();
      } else if (isSharedByMeOrSharedByStaffList) {
        openSharedByMeStream();
      }
    }

    return () => {
      closeStreams();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    organizationID,
    isAllPatientList,
    isSharedWithMeList,
    isSharedByMeOrSharedByStaffList,
  ]);

  return {
    sortByForSharedWithMe,
    sortByForSharedByMe,
    searchPatients,
    filterByDoctors,
    filterByStudyTypes,
    filterByAgeGroup,
    filterByLastModified,
    sortByForAllPatients,
    onFilterPatientsChange,
    fetchMorePatients,
  };
};
